import { z } from "zod";
import {
  BaseHttpClient,
  baseOk,
  ApiOk,
  DbDateString,
  DbTimeString,
  type MutationRequestOptions,
} from "./base";
import {
  addNoteResponseSchema,
  linkAppointmentCustomerResponseSchema,
  type LinkAppointmentCustomerRequest,
  type LinkAppointmentCustomerResponse,
  appointmentDetailSchema,
  cancelAppointmentResponseSchema,
  createAppointmentResponseSchema,
  editAppointmentPaymentResponseSchema,
  feedbackResponseSchema,
  manualCheckoutResponseSchema,
  rescheduleAppointmentResponseSchema,
  revertAppointmentResponseSchema,
  refundAppointmentResponseSchema,
  appointmentRefundabilitySchema,
  sendAppointmentNotificationResponseSchema,
  successResponseSchema,
  appointmentHistoryResponseSchema,
  appointmentHistoryVerifiedRequestSchema,
  appointmentOperatorStatusResponseSchema,
  cancelledDepositOutcomeSchema,
  type CancelledDepositOutcome,
  appointmentOperatorRescheduleResponseSchema,
  appointmentCardPaymentResponseSchema,
  cancelAppointmentCardPaymentResponseSchema,
} from "@openings/api-contract";
import type {
  AddNoteInput,
  AddNoteResponse,
  AppointmentCancellationReason,
  AppointmentDetail,
  CancelAppointmentResponse,
  CreateAppointmentResponse,
  EditAppointmentPaymentInput,
  EditAppointmentPaymentResponse,
  FeedbackResponse,
  ManualCheckoutResponse,
  RescheduleAppointmentResponse,
  RevertAppointmentInput,
  RevertAppointmentResponse,
  RefundAppointmentInput,
  RefundAppointmentResponse,
  AppointmentRefundability,
  SendAppointmentNotificationResponse,
  SuccessResponse,
  AppointmentHistoryVerifiedRequest,
  AppointmentOperatorStatusRequest,
  AppointmentOperatorStatusResponse,
  AppointmentOperatorRescheduleRequest,
  AppointmentOperatorRescheduleResponse,
  AppointmentCardPaymentResponse,
  CancelAppointmentCardPaymentRequest,
  CancelAppointmentCardPaymentResponse,
} from "@openings/api-contract";

const createAppointmentResponse = baseOk(createAppointmentResponseSchema);
const rescheduleAppointmentResponse = baseOk(
  rescheduleAppointmentResponseSchema,
);
const addNoteResponse = baseOk(addNoteResponseSchema);
const linkCustomerResponse = baseOk(linkAppointmentCustomerResponseSchema);
const cancelAppointmentResponse = baseOk(cancelAppointmentResponseSchema);
const feedbackResponse = baseOk(feedbackResponseSchema);
const successResponse = baseOk(successResponseSchema);
const manualCheckoutResponse = baseOk(manualCheckoutResponseSchema);
const editAppointmentPaymentResponse = baseOk(
  editAppointmentPaymentResponseSchema,
);
const revertAppointmentResponse = baseOk(revertAppointmentResponseSchema);
const refundAppointmentResponse = baseOk(refundAppointmentResponseSchema);
const appointmentRefundabilityResponse = baseOk(appointmentRefundabilitySchema);
const sendAppointmentNotificationResponse = baseOk(
  sendAppointmentNotificationResponseSchema,
);
const operatorStatusResponse = baseOk(appointmentOperatorStatusResponseSchema);
const cancelledDepositOutcomeResponse = baseOk(cancelledDepositOutcomeSchema);
const operatorRescheduleResponse = baseOk(
  appointmentOperatorRescheduleResponseSchema,
);

export const checkoutResponse = z.union([
  successResponse,
  manualCheckoutResponse,
]);
export const appointmentHistoryResponse = baseOk(
  appointmentHistoryResponseSchema,
);

export type AppointmentHistory = z.infer<
  typeof appointmentHistoryResponseSchema
>;

// ---- Input / Output Types ----
export interface ServiceRefInput {
  id: string;
  optionId?: string;
}

interface CreateAppointmentBaseInput {
  salonId: string;
  userId: string;
  scheduleId: string;
  customerId: string;
  teamMemberId?: string;
  paymentMethodId?: string;
  /** Server-injected by the same-origin booking proxy; never set from browser state. */
  googleAdsAutopilotAttributionSession?: string;
  services: ServiceRefInput[];
  date: DbDateString; // ISO date part
  time: DbTimeString; // e.g. "14:30"
  allowUnavailableSlot?: boolean;
  skipPaymentCollection?: boolean;
  sendNotifications?: boolean;
  verificationCode?: string;
  /** Optional override for the appointment's total duration (minutes). */
  customDurationMinutes?: number;
  /**
   * Operator-only: a walk-in's optional name or note, with the business's
   * walk-in customer as `customerId` (`customers.walkIn`).
   */
  guestLabel?: string;
  /**
   * Opaque booking metadata (string→string, ≤16 keys). Used for booking-source
   * attribution (`buildBookingSourceMetadata` from `@openings/booking-core`)
   * and audit correlation. See `appointmentMetadataSchema` in api-contract.
   */
  metadata?: Record<string, string>;
}

type AppointmentQuoteReference =
  | { requestId?: never; quotePublicId?: never }
  | {
      requestId: string;
      /** Exact public-link token required when booking a quoted request. */
      quotePublicId: string;
    };

export type CreateAppointmentInput = CreateAppointmentBaseInput &
  AppointmentQuoteReference;

export interface RescheduleAppointmentInput {
  userId: string;
  rescheduleAppointmentId: string; // previous appointment
  scheduleId: string;
  paymentMethodId?: string;
  services: ServiceRefInput[];
  date: DbDateString;
  time: DbTimeString;
  verificationCode?: string;
  phoneNumber?: string;
  email?: string;
  allowUnavailableSlot?: boolean;
  /**
   * Opaque booking metadata (string→string, ≤16 keys). When omitted the API
   * carries the previous appointment's metadata over to the replacement.
   */
  metadata?: Record<string, string>;
}

export interface CancelAppointmentInput {
  appointmentId: string;
  code: string; // verification code
  reasonCode?: AppointmentCancellationReason;
  reasonNote?: string;
}

export type AppointmentOperatorStatusInput = AppointmentOperatorStatusRequest;
export type AppointmentOperatorRescheduleInput =
  AppointmentOperatorRescheduleRequest;

export interface CheckoutAppointmentInput {
  appointmentId: string;
  price: number;
  paymentType: "cash" | "credit-card";
  tip?: number;
  tipPaymentType?: "cash" | "credit-card";
  note?: string;
  type?: "manual" | "auto";
  stripeChargeId?: string;
  stripeReceiptUrl?: string;
  receiptEmail?: string;
  /**
   * Optional offer/coupon code. When present, `price` is treated as the
   * GROSS (pre-discount) total and the server computes the discount and
   * the amount actually charged. Never send a discounted price or a
   * discount amount here.
   */
  offerCode?: string;
}

export interface SendReceiptInput {
  appointmentId: string;
  channel: "email" | "sms";
  email?: string;
}

export interface AppointmentHistoryInput {
  phoneNumber: string;
  userId: string;
  salonId: string;
}

export interface AppointmentHistoryByEmailInput {
  email: string;
  salonId: string;
}

export type AppointmentHistoryVerifiedInput = AppointmentHistoryVerifiedRequest;

export interface FeedbackInput {
  appointmentId: string;
  rating: number;
  comment?: string;
}

export type CreateAppointmentResult = ApiOk<CreateAppointmentResponse>;
export type RescheduleAppointmentResult = ApiOk<RescheduleAppointmentResponse>;
export type CancelAppointmentResult = ApiOk<CancelAppointmentResponse>;
export type FeedbackResult = ApiOk<FeedbackResponse>;
export type AddNoteResult = ApiOk<AddNoteResponse>;
export type LinkCustomerInput = LinkAppointmentCustomerRequest;
export type LinkCustomerResult = ApiOk<LinkAppointmentCustomerResponse>;
export type AppointmentHistoryResult = ApiOk<AppointmentHistory>;
export type CheckoutAppointmentResult = ApiOk<
  SuccessResponse | ManualCheckoutResponse
>;
export type EditAppointmentPaymentResult =
  ApiOk<EditAppointmentPaymentResponse>;
export type RevertAppointmentResult = ApiOk<RevertAppointmentResponse>;
export type SendAppointmentNotificationResult =
  ApiOk<SendAppointmentNotificationResponse>;
export type AppointmentDTO = AppointmentDetail;
const appointmentOk = baseOk(appointmentDetailSchema);
const sendReceiptResponse = baseOk(
  z.object({ sent: z.boolean(), channel: z.enum(["email", "sms"]) }),
);

export type SendReceiptResult = ApiOk<{
  sent: boolean;
  channel: "email" | "sms";
}>;

export type { OpenCardPayment } from "@openings/api-contract";
const cardPaymentResponse = baseOk(appointmentCardPaymentResponseSchema);
export type CardPaymentResult = ApiOk<AppointmentCardPaymentResponse>;
const cancelCardPaymentResponse = baseOk(
  cancelAppointmentCardPaymentResponseSchema,
);

export class AppointmentsClient extends BaseHttpClient {
  async create(
    input: CreateAppointmentInput,
    options?: MutationRequestOptions,
  ): Promise<CreateAppointmentResult> {
    const result = await this.post<CreateAppointmentResult>(
      input.requestId !== undefined || input.quotePublicId !== undefined
        ? "/v1/appointments/create-from-quote"
        : "/v1/appointments/create",
      input,
      createAppointmentResponse,
      options,
    );
    // BaseHttpClient retains a legacy successful-envelope fallback. Reparse
    // the safety-sensitive payload so malformed managed destinations or
    // impossible attribution dispositions never escape into browser code.
    return {
      ok: true,
      data: createAppointmentResponseSchema.parse(result.data),
    };
  }
  reschedule(
    input: RescheduleAppointmentInput,
  ): Promise<RescheduleAppointmentResult> {
    return this.post(
      "/v1/appointments/reschedule",
      input,
      rescheduleAppointmentResponse,
    );
  }
  cancelWithCode(
    input: CancelAppointmentInput,
  ): Promise<CancelAppointmentResult> {
    return this.post(
      "/v1/appointments/cancel",
      input,
      cancelAppointmentResponse,
    );
  }
  history(input: AppointmentHistoryInput): Promise<AppointmentHistoryResult> {
    const qs = new URLSearchParams({
      userId: input.userId,
      salonId: input.salonId,
    });
    return this.get(
      `/v1/appointments/history/${input.phoneNumber}?${qs.toString()}`,
      appointmentHistoryResponse,
    );
  }
  historyByEmail(
    input: AppointmentHistoryByEmailInput,
  ): Promise<AppointmentHistoryResult> {
    const qs = new URLSearchParams({
      email: input.email,
      salonId: input.salonId,
    });
    return this.get(
      `/v1/appointments/history/by-email?${qs.toString()}`,
      appointmentHistoryResponse,
    );
  }
  verifiedHistory(
    input: AppointmentHistoryVerifiedInput,
  ): Promise<AppointmentHistoryResult> {
    const body = appointmentHistoryVerifiedRequestSchema.parse(input);
    return this.post(
      "/v1/appointments/history/verified",
      body,
      appointmentHistoryResponse,
    );
  }
  feedback(input: FeedbackInput): Promise<FeedbackResult> {
    return this.post("/v1/appointments/feedback", input, feedbackResponse);
  }
  addNote(input: AddNoteInput): Promise<AddNoteResult> {
    return this.post("/v1/appointments/note", input, addNoteResponse);
  }
  /** Move a walk-in visit to a real customer (owner/member session). */
  linkCustomer(
    appointmentId: string,
    input: LinkCustomerInput,
  ): Promise<LinkCustomerResult> {
    return this.post(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/link-customer`,
      input,
      linkCustomerResponse,
    );
  }
  /** Public endpoint — no auth required. Verifies ownership via customerId. */
  addNotePublic(input: {
    appointmentId: string;
    note: string;
    customerId: string;
  }): Promise<AddNoteResult> {
    return this.post("/v1/appointments/note/public", input, addNoteResponse);
  }
  checkout(
    input: CheckoutAppointmentInput,
  ): Promise<CheckoutAppointmentResult> {
    return this.post("/v1/appointments/checkout", input, checkoutResponse);
  }
  /**
   * The appointment's open card payment, which a checkout without a card
   * refuses beside (#1053). Owner, a manager with Appointments, or the member
   * who performs it.
   */
  getCardPayment(appointmentId: string): Promise<CardPaymentResult> {
    return this.get(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/card-payment`,
      cardPaymentResponse,
    );
  }
  /** Cancel that payment on purpose. Refused (409) once it was captured. */
  cancelCardPayment(
    appointmentId: string,
    attemptId: string,
  ): Promise<ApiOk<CancelAppointmentCardPaymentResponse>> {
    const body: CancelAppointmentCardPaymentRequest = { attemptId };
    return this.post(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/card-payment/cancel`,
      body,
      cancelCardPaymentResponse,
    );
  }
  /**
   * Edit payment on a COMPLETED appointment (MANUAL checkouts only).
   * Typical error codes thrown: `VALIDATION_ERROR`, `NON_MANUAL_CHECKOUT`,
   * `UNAUTHORIZED`.
   */
  editPayment(
    appointmentId: string,
    input: Omit<EditAppointmentPaymentInput, "appointmentId">,
  ): Promise<EditAppointmentPaymentResult> {
    return this.patch(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/checkout`,
      input,
      editAppointmentPaymentResponse,
    );
  }
  /**
   * Revert a CANCELED or NO_SHOW appointment back to SCHEDULED.
   * On slot conflict the call throws with `code === "SLOT_CONFLICT"` and
   * HTTP 409. Other typical error codes: `VALIDATION_ERROR`, `UNAUTHORIZED`.
   */
  revert(
    appointmentId: string,
    input?: Omit<RevertAppointmentInput, "appointmentId">,
  ): Promise<RevertAppointmentResult> {
    return this.post(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/revert`,
      input ?? {},
      revertAppointmentResponse,
    );
  }
  /**
   * Issue a partial or full refund on a COMPLETED appointment. Requires the
   * salon owner or a team member with `canIssueRefunds`. Typical error codes:
   * `REFUND_NOT_REFUNDABLE`, `REFUND_FAILED`, `VALIDATION_ERROR`,
   * `UNAUTHORIZED`.
   */
  refund(
    appointmentId: string,
    input: Omit<RefundAppointmentInput, "appointmentId">,
    options: MutationRequestOptions & { idempotencyKey: string },
  ): Promise<ApiOk<RefundAppointmentResponse>> {
    return this.post(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/refund`,
      input,
      refundAppointmentResponse,
      options,
    );
  }
  /** Read-only refund cap + payout-reversal hint for the refund UI. */
  getRefundability(
    appointmentId: string,
    paymentSource?: RefundAppointmentInput["paymentSource"],
  ): Promise<ApiOk<AppointmentRefundability>> {
    const query = paymentSource
      ? `?paymentSource=${encodeURIComponent(paymentSource)}`
      : "";
    return this.get(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/refundability${query}`,
      appointmentRefundabilityResponse,
    );
  }
  getById(appointmentId: string): Promise<ApiOk<AppointmentDTO>> {
    return this.get(
      `/v1/appointments/${encodeURIComponent(appointmentId)}`,
      appointmentOk,
    );
  }
  /** Public endpoint — no auth required. */
  getReview(appointmentId: string): Promise<ApiOk<AppointmentDTO>> {
    return this.get(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/review`,
      appointmentOk,
    );
  }
  sendReceipt(input: SendReceiptInput): Promise<SendReceiptResult> {
    return this.post(
      "/v1/appointments/send-receipt",
      input,
      sendReceiptResponse,
    );
  }
  sendNotification(
    appointmentId: string,
  ): Promise<SendAppointmentNotificationResult> {
    return this.post(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/send-notification`,
      {},
      sendAppointmentNotificationResponse,
    );
  }
  /** Authenticated dashboard status mutation plus actor-aware notifications. */
  updateOperatorStatus(
    appointmentId: string,
    input: AppointmentOperatorStatusInput,
  ): Promise<ApiOk<AppointmentOperatorStatusResponse>> {
    return this.post(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/operator-status`,
      input,
      operatorStatusResponse,
    );
  }
  /**
   * Retry the automatic deposit refund a cancel started (same fixed key, so
   * it resumes rather than starting a second refund). Refund permission.
   */
  retryCancelledDepositRefund(
    appointmentId: string,
  ): Promise<ApiOk<CancelledDepositOutcome>> {
    return this.post(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/cancel-deposit-refund/retry`,
      {},
      cancelledDepositOutcomeResponse,
    );
  }
  /** Authenticated dashboard cancel-and-replace plus actor-aware notifications. */
  operatorReschedule(
    appointmentId: string,
    input: AppointmentOperatorRescheduleInput,
  ): Promise<ApiOk<AppointmentOperatorRescheduleResponse>> {
    return this.post(
      `/v1/appointments/${encodeURIComponent(appointmentId)}/operator-reschedule`,
      input,
      operatorRescheduleResponse,
    );
  }
}
